/**
 * @file PlanArchive.tsx
 * @description Component for displaying the archive of editorial plans.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";

import config from "../../config/config";
import { auth } from "../../config/firebaseConfig";
import log from "../../config/logging";

import Loading from "../../components/common/Loading";
import PlanItem from "../../components/common/PlanItem";
import AlphabetBackground from "../../components/common/AlphabetBackground";
import PlanIcon from "../../components/common/icons/PlanIcon";
import { PlanDetails } from "../../types";

const PlanArchive: React.FC = () => {
  const [user, loading] = useAuthState(auth);
  const [plans, setPlans] = useState<PlanDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      if (loading) return;

      if (!user) {
        log.warn("User not authenticated, redirecting to login");
        toast.error("Per favore, accedi per visualizzare questa pagina");
        navigate("/accedi");
        return;
      }

      try {
        const token = await user.getIdToken(true);
        const response = await fetch(
          `${config.backendUrl}/api/plans?userId=${user.uid}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Errore: ${response.statusText}`);
        }

        const data = await response.json();
        log.debug("Plans fetched successfully", { data });

        setPlans(data.plans);
      } catch (error: unknown) {
        log.error("Error fetching plans", { error });
        toast.error("Errore durante il recupero dei piani editoriali");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, [user, loading, navigate]);

  const handlePlanUpdated = (updatedPlan: PlanDetails) => {
    setPlans((prevPlans) =>
      prevPlans.map((plan) => (plan.id === updatedPlan.id ? updatedPlan : plan))
    );
  };

  if (loading || isLoading) {
    return <Loading />;
  }

  return (
    <section className="mx-auto max-w-content">
      <AlphabetBackground fill="#e7dddc" opacity={0.5} />
      <PlanIcon className="relative w-32 mx-auto mb-2" />
      <h2 className="text-2xl !font-medium text-primary !mb-20 text-center relative">
        Archivio piani editoriali
      </h2>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] gap-4 justify-items-stretch list-none p-0 relative">
        {plans.map((plan) => (
          <PlanItem key={plan.id} plan={plan} onPlanUpdated={handlePlanUpdated} />
        ))}
      </ul>
    </section>
  );
};

export default PlanArchive;
